import { useParams } from "react-router";

import { Card } from "../../../common/Atoms/Card";
import { classNames } from "../../../common/lib/classNames";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { useUnpaidInvoicesBoard } from "../../hooks/useUnpaidInvoicesBoard";
import { InvoiceAgingBucket } from "../../lib/invoiceAgingBuckets";

import { InvoiceKanbanColumn } from "./InvoiceKanbanColumn";
import { InvoiceKanbanFilterBar } from "./InvoiceKanbanFilterBar";

interface InvoiceKanbanBoardProps {
  fitContent?: boolean;
  onBucketHeadingClick?: (bucket: InvoiceAgingBucket) => void;
}

export function InvoiceKanbanBoard({
  fitContent = false,
  onBucketHeadingClick,
}: InvoiceKanbanBoardProps) {
  const { organisationId } = useParams<{ organisationId: string }>();
  const {
    columns,
    error,
  } = useUnpaidInvoicesBoard();
  const hasColumnError = Boolean(error) || columns.some(column => Boolean(column.error));

  if (!organisationId) {
    return null;
  }

  const boardBody = (
    <>
      <InvoiceKanbanFilterBar />

      { hasColumnError ? (
        <div className={ `mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2` }>
          <Paragraph
            variant={ `error` }
          >
            { `Unable to load board invoices.` }
          </Paragraph>
        </div>
      ) : null }

      <div className={ classNames(
        `overflow-x-auto`,
        fitContent ? `` : `min-h-0 flex-1`,
      ) }>
        <div className={ classNames(
          `inline-flex items-stretch gap-4 py-0.5`,
          fitContent ? `` : `h-full`,
        ) }>
          { columns.map(column => (
            <InvoiceKanbanColumn
              key={ column.key }
              label={ column.label }
              accentColor={ column.accentColor }
              invoices={ column.invoices }
              count={ column.count }
              outstandingTotalHomeCents={ column.outstandingTotalHomeCents }
              loadedCount={ column.loadedCount }
              hasMore={ column.hasMore }
              organisationId={ organisationId }
              isLoading={ column.isLoading && column.loadedCount === 0 }
              isFetching={ column.isFetching }
              onLoadMore={ column.loadMore }
              onHeadingClick={ onBucketHeadingClick ? () => onBucketHeadingClick(column) : undefined }
              fitContent={ fitContent }
            />
          )) }
        </div>
      </div>
    </>
  );

  // Contact: no card chrome. Main: white surface so column wells don't blend into page gray.
  if (fitContent) {
    return (
      <div className={ `flex flex-col` }>
        { boardBody }
      </div>
    );
  }

  return (
    <Card className={ `flex h-full min-h-0 flex-col overflow-hidden` }>
      { boardBody }
    </Card>
  );
}
